import { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import { CalendarDays, Plus, Pencil, Trash2, MapPin, ImagePlus, X, Lock, Sparkles, Wand2, Calendar as CalendarIcon } from "lucide-react";

const fileToDataUrl = (file) => new Promise((res, rej) => {
  const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
});

// Sélecteur date (calendrier français Shadcn) + heure séparée. Valeur = "YYYY-MM-DDTHH:mm".
function DateTimeField({ value, onChange, testId, clearable = false }) {
  const [open, setOpen] = useState(false);
  const datePart = value ? value.slice(0, 10) : "";
  const timePart = value ? value.slice(11, 16) : "";
  const selectedDate = datePart ? parseISO(datePart) : undefined;
  const today = new Date(); today.setHours(0, 0, 0, 0);

  const setDate = (d) => {
    if (!d) return;
    onChange(`${format(d, "yyyy-MM-dd")}T${timePart || "18:00"}`);
    setOpen(false);
  };
  const setTime = (t) => {
    if (!datePart) { toast.error("Choisissez d'abord une date."); return; }
    onChange(`${datePart}T${t}`);
  };

  return (
    <div className="flex gap-2 mt-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" className="flex-1 justify-start font-normal" data-testid={testId}>
            <CalendarIcon size={15} className="mr-2 text-slate-500" />
            {selectedDate ? format(selectedDate, "d MMMM yyyy", { locale: fr }) : <span className="text-slate-500">Choisir une date</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" selected={selectedDate} onSelect={setDate} locale={fr}
                    disabled={{ before: today }} initialFocus />
        </PopoverContent>
      </Popover>
      <Input type="time" value={timePart} onChange={(e) => setTime(e.target.value)} className="w-28" data-testid={`${testId}-time`} />
      {clearable && value && (
        <Button type="button" variant="ghost" size="icon" onClick={() => onChange("")}
                className="text-slate-400 hover:text-rose-600 shrink-0" data-testid={`${testId}-clear`}>
          <X size={16} />
        </Button>
      )}
    </div>
  );
}

const fmtEventDate = (iso) => {
  try { return format(parseISO(iso), "EEE d MMMM yyyy 'à' HH'h'mm", { locale: fr }); }
  catch { return (iso || "").slice(0, 16).replace("T", " à "); }
};


const EMPTY = { title: "", description: "", start_date: "", end_date: "", city: "", address: "", category: "animation", image: "", url: "" };

export default function AssocEvents() {
  const ctx = useOutletContext() || {};
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = fermé, {} = nouveau, {...} = édition
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [topic, setTopic] = useState("");
  const [aiTextBusy, setAiTextBusy] = useState(false);
  const imgRef = useRef();
  const active = ctx.asso ? ctx.asso.status === "active" : true;

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/associations/me/events"); setItems(data.items || []); }
    catch { setItems([]); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    api.get("/associations/categories").then((r) => setCats(r.data.event_categories || [])).catch(() => {});
    load();
  }, []);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const openNew = () => { setForm(EMPTY); setTopic(""); setEditing({}); };
  const openEdit = (e) => {
    setForm({
      title: e.title || "", description: e.description || "",
      start_date: (e.start_date || "").slice(0, 16), end_date: (e.end_date || "").slice(0, 16),
      city: e.city || "", address: e.address || "", category: e.category || "animation",
      image: e.image || "", url: e.url || "",
    });
    setEditing(e);
  };
  const close = () => { setEditing(null); setForm(EMPTY); setTopic(""); };

  const genText = async () => {
    if (!topic || topic.length < 2) { toast.error("Décrivez le sujet de votre événement."); return; }
    setAiTextBusy(true);
    try {
      const { data } = await api.post("/associations/me/ai/event", {
        topic, date_hint: form.start_date || "", place_hint: [form.address, form.city].filter(Boolean).join(", "), tone: "chaleureux",
      });
      setForm((s) => ({ ...s, title: data.title || s.title, description: data.description || s.description }));
      toast.success("Annonce rédigée par l'IA ! Relisez et ajustez.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Génération impossible, réessayez."); }
    finally { setAiTextBusy(false); }
  };

  const pickImage = async () => {
    const f = imgRef.current?.files?.[0]; if (!f) return;
    if (f.size > 6 * 1024 * 1024) { toast.error("Image trop lourde (max 6 Mo)."); return; }
    set("image", await fileToDataUrl(f));
  };

  const genImage = async () => {
    if (!form.title) { toast.error("Donnez d'abord un titre à l'événement."); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/associations/me/ai/image", { subject: form.title, style: "photo" });
      set("image", data.image_url);
      toast.success("Visuel généré par l'IA !");
    } catch (err) { toast.error(err?.response?.data?.detail || "Génération d'image impossible."); }
    finally { setAiBusy(false); }
  };

  const save = async () => {
    if (!form.title || form.title.length < 2) { toast.error("Le titre est requis."); return; }
    if (!form.start_date) { toast.error("La date de début est requise."); return; }
    if (form.end_date && form.end_date < form.start_date) { toast.error("La date de fin doit être après la date de début."); return; }
    if (!form.city) { toast.error("La ville est requise."); return; }
    setBusy(true);
    try {
      if (editing && editing.id) {
        await api.patch(`/associations/me/events/${editing.id}`, form);
        toast.success("Événement mis à jour !");
      } else {
        await api.post("/associations/me/events", form);
        toast.success("Événement publié dans l'agenda !");
      }
      close(); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur à l'enregistrement."); }
    finally { setBusy(false); }
  };

  const remove = async (e) => {
    if (!window.confirm(`Supprimer « ${e.title} » ?`)) return;
    try { await api.delete(`/associations/me/events/${e.id}`); toast.success("Événement supprimé."); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Suppression impossible."); }
  };

  return (
    <div className="max-w-4xl" data-testid="assoc-events">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold">Mes événements</h1>
          <p className="text-slate-500 text-sm mt-1">Vos événements apparaissent dans l'agenda <b>Sortir</b> et dans l'agenda associatif.</p>
        </div>
        <Button onClick={openNew} disabled={!active} className="btn-shine text-white" data-testid="assoc-event-new"><Plus size={16} className="mr-1" /> Nouvel événement</Button>
      </div>

      {!active && (
        <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800 flex items-center gap-2" data-testid="assoc-events-locked">
          <Lock size={16} /> La publication d'événements sera débloquée dès la validation de votre page par notre équipe.
        </div>
      )}

      {editing && (
        <div className="mt-5 bg-white border border-slate-200 rounded-2xl p-5 space-y-4" data-testid="assoc-event-form">
          <h2 className="font-heading font-bold">{editing.id ? "Modifier l'événement" : "Nouvel événement"}</h2>
          <div className="bg-violet-50 border border-violet-200 rounded-xl p-4" data-testid="assoc-event-ai-block">
            <div className="flex items-center gap-2 font-heading font-bold text-violet-800"><Wand2 size={18} /> Assistant IA</div>
            <p className="text-sm text-violet-700/80 mt-1">Décrivez votre événement, l'IA propose un titre et une description attractive.</p>
            <div className="mt-3 grid sm:grid-cols-[1fr_auto] gap-2">
              <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Ex : vide-grenier, tournoi de foot, concert caritatif…" data-testid="assoc-event-ai-topic" />
              <Button type="button" onClick={genText} disabled={aiTextBusy} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="assoc-event-ai-generate">
                <Sparkles size={15} className="mr-1" /> {aiTextBusy ? "Rédaction…" : "Rédiger"}
              </Button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label>Titre *</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="assoc-event-title" /></div>
            <div><Label>Date de début *</Label><DateTimeField value={form.start_date} onChange={(v) => set("start_date", v)} testId="assoc-event-start" /></div>
            <div><Label>Date de fin</Label><DateTimeField value={form.end_date} onChange={(v) => set("end_date", v)} testId="assoc-event-end" clearable /></div>
            <div><Label>Ville *</Label><Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="assoc-event-city" /></div>
            <div>
              <Label>Catégorie</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="assoc-event-category"><SelectValue /></SelectTrigger>
                <SelectContent>{cats.map((c) => (<SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2"><Label>Adresse / lieu</Label><Input value={form.address} onChange={(e) => set("address", e.target.value)} /></div>
            <div className="sm:col-span-2"><Label>Description</Label><Textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} /></div>
            <div className="sm:col-span-2"><Label>Lien (billetterie, infos…)</Label><Input value={form.url} onChange={(e) => set("url", e.target.value)} placeholder="https://…" /></div>
            <div className="sm:col-span-2">
              <Label>Visuel</Label>
              <div className="mt-1 flex items-center gap-3">
                <div className="w-28 h-16 rounded-xl bg-slate-100 overflow-hidden grid place-items-center shrink-0">
                  {form.image ? <img src={form.image} alt="" className="w-full h-full object-cover" /> : <ImagePlus className="text-slate-300" />}
                </div>
                <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={pickImage} />
                <Button type="button" variant="outline" size="sm" onClick={() => imgRef.current?.click()}><ImagePlus size={14} className="mr-1" /> Choisir</Button>
                <Button type="button" variant="outline" size="sm" onClick={genImage} disabled={aiBusy} data-testid="assoc-event-ai-image"><Sparkles size={14} className="mr-1" /> {aiBusy ? "Génération…" : "Générer (IA)"}</Button>
                {form.image && <button type="button" onClick={() => set("image", "")} className="text-slate-400 hover:text-rose-600"><X size={16} /></button>}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={close}>Annuler</Button>
            <Button onClick={save} disabled={busy} className="btn-shine text-white" data-testid="assoc-event-save">{busy ? "Enregistrement…" : "Enregistrer"}</Button>
          </div>
        </div>
      )}

      <div className="mt-6 space-y-3">
        {loading ? <div className="text-slate-500 text-sm">Chargement…</div> : items.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-10 text-center text-slate-500" data-testid="assoc-events-empty">
            <CalendarDays size={30} className="mx-auto text-slate-300 mb-2" />
            Aucun événement pour l'instant. {active ? "Créez votre premier événement !" : ""}
          </div>
        ) : items.map((e) => (
          <div key={e.id} className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-4" data-testid={`assoc-event-row-${e.id}`}>
            <div className="w-16 h-16 rounded-xl bg-slate-100 overflow-hidden shrink-0 grid place-items-center">
              {e.image ? <img src={e.image} alt="" className="w-full h-full object-cover" /> : <CalendarDays className="text-slate-300" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-slate-900 truncate">{e.title}</div>
              <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
                <span>{fmtEventDate(e.start_date)}</span>
                <span className="inline-flex items-center gap-1"><MapPin size={12} /> {e.city}</span>
                <span className="px-2 py-0.5 rounded-full bg-slate-100">{e.category_label}</span>
              </div>
            </div>
            <div className="flex gap-1 shrink-0">
              <Button variant="ghost" size="sm" onClick={() => openEdit(e)} data-testid={`assoc-event-edit-${e.id}`}><Pencil size={15} /></Button>
              <Button variant="ghost" size="sm" onClick={() => remove(e)} className="text-rose-600 hover:text-rose-700" data-testid={`assoc-event-delete-${e.id}`}><Trash2 size={15} /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
